import type { WanCandidate } from '@simpleacs/shared';

/**
 * The WAN card's Delete, as rules and one sequence — kept here, out of the
 * card, so they are pinned without a page.
 *
 * A bridge is deletable like any other connection (operator, 2026-10-14:
 * "2 wan bridges, one not deletable" … "its just a bridge, we add wifi and
 * lan ports where needed"). Only the management connection never is — the
 * platform reaches the router over it.
 */
export function wanDeletable(c: WanCandidate): boolean {
  return !c.management && c.role !== 'management';
}

/** The WiFi networks a bridge carries, by number ("1", "5"), from what the router says it serves. */
export function bridgeWifi(c: WanCandidate): string[] {
  if (c.role !== 'bridge') return [];
  return c.serves.map((s) => /^WiFi (\d+)$/.exec(s)?.[1]).filter((n): n is string => Boolean(n));
}

const isAre = (n: number) => (n === 1 ? 'is' : 'are');

/** What the confirm step says before the second press: what goes, and what happens to whatever rode it. */
export function wanDeleteWarning(c: WanCandidate): string {
  const label = c.label || 'this WAN';
  const on = c.vlanId && c.vlanId !== '0' ? ` on VLAN ${c.vlanId}` : '';
  if (c.role !== 'bridge') {
    return `Delete ${label}${on}? The subscriber loses this connection, and nothing builds it back until the WAN is applied again.`;
  }
  const wifi = bridgeWifi(c);
  const lans = c.serves.filter((s) => s.startsWith('LAN '));
  return [
    `Delete the bridge ${label}${on}?`,
    wifi.length > 0
      ? `WiFi ${wifi.join(', ')} ${isAre(wifi.length)} taken off it and switched off first — bound to nothing, a network would join the subscriber's own LAN.`
      : '',
    lans.length > 0 ? `${lans.join(', ')} ${isAre(lans.length)} released from it.` : '',
    'Nothing builds it back until the WAN is applied again.',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * The calls, in order: each WiFi network the bridge carries is detached
 * first — the bridge card's own Detach (off the bridge and switched off on
 * the router, its socket off the VLAN on the OLT and out of the record) —
 * and only then is the connection deleted. The server refuses a bridge that
 * still carries WiFi, so a detach that fails stops the sequence with the
 * bridge left in place. `api` is injected so the order is pinned without a
 * network.
 */
export async function runWanDelete(
  c: WanCandidate,
  endpoints: { delete: string; deviceId: string | null },
  api: {
    post: <T>(path: string, body?: unknown) => Promise<T>;
    delete: <T>(path: string) => Promise<T>;
  },
): Promise<{ message: string }> {
  const notes: string[] = [];
  for (const n of bridgeWifi(c)) {
    if (!endpoints.deviceId) {
      throw new Error(`WiFi ${n} rides ${c.label || 'this bridge'}, and no router is linked to take it off, so nothing was deleted.`);
    }
    const detached = await api.post<{ message: string }>(
      `/devices/${encodeURIComponent(endpoints.deviceId)}/wifi/${encodeURIComponent(n)}/detach`,
      {},
    );
    notes.push(detached.message);
  }
  const deleted = await api.delete<{ message: string }>(`${endpoints.delete}?path=${encodeURIComponent(c.path)}`);
  return { message: [deleted.message, ...notes].join(' ') };
}
